/**
 * Conteúdo de ajuda contextual para termos técnicos
 * 
 * Este arquivo contém explicações em português para termos técnicos
 * usados na plataforma, especialmente na área de Tokenização.
 */

export interface HelpTerm {
  term: string;
  title: string;
  description: string;
  example?: string;
  learnMoreUrl?: string;
}

export interface HelpCategory {
  id: string;
  name: string;
  terms: HelpTerm[];
}

/**
 * Glossário de termos de Tokenização
 */
export const tokenizationGlossary: HelpTerm[] = [
  {
    term: 'RWA',
    title: 'Real World Assets (Ativos do Mundo Real)',
    description: 'São ativos físicos ou tradicionais (como imóveis, recebíveis, commodities) que são representados digitalmente através de tokens na blockchain. Isso permite fracionar a propriedade e facilitar a negociação.',
    example: 'Um imóvel de R$ 1 milhão pode ser tokenizado em 1.000 tokens de R$ 1.000 cada, permitindo que investidores comprem frações do imóvel.',
  },
  {
    term: 'Supply',
    title: 'Fornecimento de Tokens',
    description: 'Quantidade total de tokens que existem ou podem existir para um determinado ativo. Inclui o supply circulante (em posse de investidores) e o supply máximo (limite total que pode ser emitido).',
    example: 'Um ativo pode ter supply máximo de 10.000 tokens, com 5.000 já emitidos (circulante) e 5.000 ainda disponíveis para emissão.',
  },
  {
    term: 'Yield',
    title: 'Rendimento',
    description: 'Retorno financeiro gerado pelo ativo tokenizado, distribuído proporcionalmente aos detentores dos tokens. Pode vir de aluguéis, juros, dividendos ou valorização do ativo.',
    example: 'Um token de imóvel pode distribuir yield mensal de 0,8% proveniente dos aluguéis recebidos.',
  },
  {
    term: 'Mint',
    title: 'Emissão de Tokens',
    description: 'Processo de criar novos tokens na blockchain. Só pode ser feito quando há lastro (garantia) suficiente para cobrir os novos tokens emitidos.',
    example: 'Ao receber mais R$ 100.000 em custódia, o emissor pode "mintar" 100 novos tokens de R$ 1.000 cada.',
  },
  {
    term: 'Burn',
    title: 'Queima de Tokens',
    description: 'Processo de destruir tokens permanentemente, removendo-os de circulação. Geralmente feito quando o ativo subjacente é vendido ou resgatado.',
    example: 'Quando um investidor resgata seus tokens por dinheiro, os tokens são "queimados" para manter o lastro correto.',
  },
  {
    term: 'Smart Contract',
    title: 'Contrato Inteligente',
    description: 'Programa de computador que executa automaticamente os termos de um contrato quando condições predefinidas são atendidas. Roda na blockchain de forma transparente e imutável.',
    example: 'Um smart contract pode distribuir automaticamente os rendimentos para todos os holders no dia do pagamento.',
  },
  {
    term: 'Compliance',
    title: 'Conformidade Regulatória',
    description: 'Conjunto de regras e verificações para garantir que as operações estejam em conformidade com as leis e regulamentos aplicáveis, incluindo KYC (Know Your Customer) e AML (Anti-Money Laundering).',
    example: 'Antes de comprar tokens, o investidor precisa passar pela verificação de compliance para confirmar sua identidade.',
  },
  {
    term: 'Whitelist',
    title: 'Lista de Aprovados',
    description: 'Lista de endereços/investidores autorizados a participar de determinada operação. Usado para garantir que apenas investidores verificados possam comprar ou vender tokens.',
    example: 'Somente investidores na whitelist podem participar da oferta inicial de tokens.',
  },
  {
    term: 'Proof of Reserve',
    title: 'Prova de Reserva',
    description: 'Mecanismo que comprova que os tokens emitidos são lastreados por ativos reais em custódia. Pode ser verificado publicamente através de oráculos na blockchain.',
    example: 'O oráculo Chainlink verifica diariamente que há R$ 1 milhão em custódia para os 1.000 tokens emitidos.',
  },
  {
    term: 'Oracle',
    title: 'Oráculo',
    description: 'Serviço que fornece dados externos para a blockchain, como preços de ativos, taxas de câmbio ou verificação de reservas. Permite que smart contracts acessem informações do mundo real.',
    example: 'O oráculo de preços atualiza o valor do token a cada hora com base na avaliação do imóvel.',
  },
  {
    term: 'Token',
    title: 'Token Digital',
    description: 'Representação digital de um ativo ou direito na blockchain. Pode representar propriedade fracionada, direitos de voto, ou acesso a serviços.',
    example: 'Cada token representa 0,1% de propriedade do imóvel tokenizado.',
  },
  {
    term: 'Holder',
    title: 'Detentor de Tokens',
    description: 'Pessoa ou entidade que possui tokens em sua carteira digital. Os holders têm direito aos benefícios associados aos tokens, como rendimentos e valorização.',
    example: 'Os holders do token IMOVEL01 receberão os rendimentos proporcionais à sua participação.',
  },
  {
    term: 'Deploy',
    title: 'Implantação',
    description: 'Processo de publicar um smart contract na blockchain, tornando-o ativo e disponível para uso. Uma vez deployado, o contrato não pode ser alterado.',
    example: 'Após a auditoria, o smart contract foi deployado na rede Ethereum.',
  },
  {
    term: 'ERC-20',
    title: 'Padrão de Token Fungível',
    description: 'Padrão técnico para tokens na blockchain Ethereum. Tokens ERC-20 são fungíveis, ou seja, cada unidade é idêntica e intercambiável.',
    example: 'O token do imóvel segue o padrão ERC-20, permitindo negociação em exchanges.',
  },
  {
    term: 'ERC-1400',
    title: 'Padrão de Security Token',
    description: 'Padrão para tokens que representam valores mobiliários (securities). Inclui funcionalidades de compliance como whitelist, restrições de transferência e partições.',
    example: 'Tokens de recebíveis usam ERC-1400 para garantir conformidade com a CVM.',
  },
  {
    term: 'Custody',
    title: 'Custódia',
    description: 'Guarda segura dos ativos que lastreiam os tokens. A custódia pode ser feita por instituições financeiras autorizadas ou através de contratos inteligentes.',
    example: 'Os documentos do imóvel estão em custódia no banco XYZ, garantindo o lastro dos tokens.',
  },
  {
    term: 'AUM',
    title: 'Assets Under Management',
    description: 'Valor total dos ativos sob gestão. No contexto de tokenização, representa o valor total de todos os ativos tokenizados na plataforma.',
    example: 'A plataforma tem R$ 50 milhões em AUM distribuídos em 20 ativos tokenizados.',
  },
  {
    term: 'TVL',
    title: 'Total Value Locked',
    description: 'Valor total bloqueado em contratos inteligentes. Indica quanto capital está comprometido em protocolos DeFi ou tokens.',
    example: 'O TVL do protocolo de staking é de R$ 10 milhões.',
  },
];


/**
 * Glossário de termos de DeFi e Crypto
 */
export const defiGlossary: HelpTerm[] = [
  {
    term: 'Staking',
    title: 'Staking (Participação)',
    description: 'Processo de bloquear criptomoedas para participar da validação de transações na rede e receber recompensas. Similar a um depósito a prazo.',
    example: 'Ao fazer staking de 1.000 tokens, você recebe 5% ao ano em recompensas.',
  },
  {
    term: 'Yield Farming',
    title: 'Agricultura de Rendimentos',
    description: 'Estratégia de maximizar rendimentos movendo ativos entre diferentes protocolos DeFi para obter as melhores taxas de retorno.',
    example: 'O yield farming pode gerar 10-20% ao ano, mas envolve riscos de smart contract.',
  },
  {
    term: 'Liquidity Pool',
    title: 'Pool de Liquidez',
    description: 'Reserva de tokens bloqueados em um smart contract que permite trocas descentralizadas. Provedores de liquidez recebem taxas das transações.',
    example: 'Ao adicionar liquidez ao pool ETH/USDC, você recebe parte das taxas de swap.',
  },
  {
    term: 'DEX',
    title: 'Exchange Descentralizada',
    description: 'Plataforma de negociação que opera sem intermediários, usando smart contracts para executar trades diretamente entre usuários.',
    example: 'Uniswap e PancakeSwap são exemplos de DEXs populares.',
  },
  {
    term: 'Swap',
    title: 'Troca de Tokens',
    description: 'Operação de trocar um token por outro diretamente, sem precisar converter para moeda fiduciária primeiro.',
    example: 'Você pode fazer swap de ETH para USDC instantaneamente na DEX.',
  },
  {
    term: 'Bridge',
    title: 'Ponte entre Blockchains',
    description: 'Protocolo que permite transferir ativos entre diferentes blockchains, como de Ethereum para Polygon.',
    example: 'Use a bridge para mover seus tokens de Ethereum para Polygon e pagar menos taxas.',
  },
  {
    term: 'Gas',
    title: 'Taxa de Transação',
    description: 'Taxa paga para processar transações na blockchain. O valor varia conforme a demanda da rede.',
    example: 'A transação custou 0.005 ETH em gas fees.',
  },
  {
    term: 'Wallet',
    title: 'Carteira Digital',
    description: 'Software ou hardware que armazena suas chaves privadas e permite interagir com a blockchain para enviar, receber e gerenciar criptoativos.',
    example: 'MetaMask é uma carteira digital popular para interagir com DeFi.',
  },
];

/**
 * Glossário de termos bancários
 */
export const bankingGlossary: HelpTerm[] = [
  {
    term: 'KYC',
    title: 'Know Your Customer',
    description: 'Processo de verificação de identidade do cliente exigido por regulamentações financeiras. Inclui validação de documentos e dados pessoais.',
    example: 'O KYC inclui envio de RG, comprovante de residência e selfie.',
  },
  {
    term: 'AML',
    title: 'Anti-Money Laundering',
    description: 'Conjunto de procedimentos para prevenir lavagem de dinheiro, incluindo monitoramento de transações suspeitas e reporte às autoridades.',
    example: 'O sistema AML detectou uma transação atípica e solicitou verificação adicional.',
  },
  {
    term: 'Open Finance',
    title: 'Sistema Financeiro Aberto',
    description: 'Iniciativa que permite compartilhamento de dados financeiros entre instituições com consentimento do cliente, promovendo competição e inovação.',
    example: 'Com Open Finance, você pode ver todas suas contas de diferentes bancos em um só lugar.',
  },
  {
    term: 'PIX',
    title: 'Pagamento Instantâneo',
    description: 'Sistema de pagamentos instantâneos do Banco Central do Brasil que permite transferências 24/7 em segundos.',
    example: 'O PIX permite transferir dinheiro a qualquer hora, inclusive fins de semana.',
  },
];


/**
 * Glossário de termos de Compliance e PLD/FT
 */
export const complianceGlossary: HelpTerm[] = [
  {
    term: 'COAF',
    title: 'Conselho de Controle de Atividades Financeiras',
    description: 'Órgão do governo federal responsável por receber, examinar e identificar ocorrências suspeitas de lavagem de dinheiro e financiamento ao terrorismo.',
    example: 'Comunicações ao COAF são obrigatórias para operações acima de R$ 50.000 em espécie.',
  },
  {
    term: 'ROS',
    title: 'Relatório de Operações Suspeitas',
    description: 'Documento enviado ao COAF quando uma instituição financeira identifica operações que possam configurar indícios de lavagem de dinheiro ou financiamento ao terrorismo.',
    example: 'O compliance officer gerou um ROS após identificar movimentações atípicas na conta do cliente.',
  },
  {
    term: 'DOF',
    title: 'Declaração de Operações Financeiras',
    description: 'Comunicação obrigatória ao COAF de operações financeiras que ultrapassam determinados limites estabelecidos pela regulamentação.',
    example: 'Toda operação em espécie acima de R$ 50.000 gera automaticamente uma DOF.',
  },
  {
    term: 'MED',
    title: 'Mecanismo Especial de Devolução',
    description: 'Procedimento do PIX que permite a devolução de valores em casos de fraude ou falha operacional, com prazos e regras definidos pelo Banco Central.',
    example: 'O cliente solicitou um MED após identificar uma transferência PIX não autorizada em sua conta.',
  },
  {
    term: 'PEP',
    title: 'Pessoa Exposta Politicamente',
    description: 'Pessoa que ocupa ou ocupou cargo público relevante nos últimos 5 anos, sujeita a monitoramento reforçado por risco de corrupção e lavagem de dinheiro.',
    example: 'Clientes classificados como PEP passam por due diligence reforçada na abertura de conta.',
  },
  {
    term: 'PLD/FT',
    title: 'Prevenção à Lavagem de Dinheiro e Financiamento ao Terrorismo',
    description: 'Conjunto de políticas, procedimentos e controles internos adotados por instituições financeiras para prevenir e detectar atividades ilícitas.',
    example: 'A política de PLD/FT da instituição exige monitoramento contínuo de todas as transações.',
  },
  {
    term: 'Estruturação',
    title: 'Estruturação (Smurfing)',
    description: 'Prática ilícita de fracionar operações financeiras em valores menores para evitar os limites de comunicação obrigatória aos órgãos reguladores.',
    example: 'O sistema detectou possível estruturação: 10 depósitos de R$ 9.900 em dias consecutivos.',
  },
  {
    term: 'Comunicação Suspeita',
    title: 'Comunicação de Operação Suspeita',
    description: 'Notificação enviada ao COAF quando há indícios de que uma operação pode estar relacionada a lavagem de dinheiro ou financiamento ao terrorismo.',
    example: 'A área de compliance enviou uma comunicação suspeita referente a transferências internacionais sem justificativa econômica.',
  },
  {
    term: 'Investidor Qualificado',
    title: 'Investidor Qualificado',
    description: 'Pessoa física ou jurídica que possui investimentos financeiros superiores a R$ 1 milhão ou certificação técnica aprovada pela CVM.',
    example: 'Apenas investidores qualificados podem participar de ofertas restritas de tokens de valores mobiliários.',
  },
  {
    term: 'Restrição Geográfica',
    title: 'Restrição Geográfica',
    description: 'Limitação de acesso a determinados produtos ou serviços financeiros com base na localização geográfica do investidor, conforme regulamentações locais.',
    example: 'Tokens de securities não podem ser vendidos para investidores em jurisdições não autorizadas.',
  },
];


/**
 * Glossário de termos de Crédito
 */
export const creditGlossary: HelpTerm[] = [
  {
    term: 'CET',
    title: 'Custo Efetivo Total',
    description: 'Taxa que representa o custo total de um empréstimo ou financiamento, incluindo juros, tarifas, tributos e seguros. Permite comparar ofertas de crédito de diferentes instituições.',
    example: 'O empréstimo tem taxa de juros de 1,5% ao mês, mas o CET é de 1,8% ao mês considerando IOF e tarifas.',
  },
  {
    term: 'SAC',
    title: 'Sistema de Amortização Constante',
    description: 'Método de amortização onde o valor da amortização é fixo em todas as parcelas, resultando em parcelas decrescentes ao longo do tempo.',
    example: 'No SAC, a primeira parcela é R$ 1.500 e a última R$ 800, pois os juros diminuem conforme o saldo devedor reduz.',
  },
  {
    term: 'Price',
    title: 'Tabela Price (Sistema Francês)',
    description: 'Método de amortização onde todas as parcelas têm o mesmo valor. No início, a maior parte da parcela é composta por juros; ao final, por amortização.',
    example: 'Na Tabela Price, todas as 12 parcelas do empréstimo são de R$ 1.200.',
  },
  {
    term: 'IOF',
    title: 'Imposto sobre Operações Financeiras',
    description: 'Tributo federal incidente sobre operações de crédito, câmbio, seguros e títulos. No crédito, é cobrado sobre o valor financiado.',
    example: 'O IOF do empréstimo pessoal foi de R$ 150, calculado sobre o valor de R$ 10.000.',
  },
  {
    term: 'SCR',
    title: 'Sistema de Informações de Crédito',
    description: 'Base de dados do Banco Central que registra todas as operações de crédito dos clientes no sistema financeiro, usada para análise de risco.',
    example: 'A consulta ao SCR mostrou que o cliente possui R$ 50.000 em dívidas ativas em outras instituições.',
  },
  {
    term: 'Score',
    title: 'Score de Crédito',
    description: 'Pontuação numérica que indica a probabilidade de um cliente honrar seus compromissos financeiros. Quanto maior o score, menor o risco de inadimplência.',
    example: 'O cliente tem score 750 (de 0 a 1000), classificado como baixo risco de crédito.',
  },
  {
    term: 'Inadimplência',
    title: 'Inadimplência',
    description: 'Situação em que o devedor não cumpre suas obrigações de pagamento no prazo acordado. Pode resultar em juros moratórios, multa e negativação.',
    example: 'Após 90 dias de inadimplência, o cliente foi negativado nos bureaus de crédito.',
  },
  {
    term: 'Parcela',
    title: 'Parcela',
    description: 'Valor periódico a ser pago pelo devedor, composto por amortização do principal e juros. A composição varia conforme o sistema de amortização escolhido.',
    example: 'O empréstimo de R$ 24.000 será pago em 12 parcelas mensais de R$ 2.200.',
  },
  {
    term: 'Amortização',
    title: 'Amortização',
    description: 'Processo de redução gradual do saldo devedor de um empréstimo através de pagamentos periódicos. É a parte da parcela que efetivamente reduz a dívida.',
    example: 'Na primeira parcela de R$ 1.200, R$ 800 são de amortização e R$ 400 de juros.',
  },
  {
    term: 'Garantia',
    title: 'Garantia',
    description: 'Bem ou direito oferecido pelo devedor para assegurar o pagamento de uma dívida. Pode ser real (imóvel, veículo) ou fidejussória (fiador, aval).',
    example: 'O financiamento imobiliário tem o próprio imóvel como garantia (alienação fiduciária).',
  },
];


/**
 * Glossário de termos de Cartões
 */
export const cardsGlossary: HelpTerm[] = [
  {
    term: 'BIN',
    title: 'Bank Identification Number',
    description: 'Primeiros 6 a 8 dígitos do número do cartão que identificam a instituição emissora e a bandeira. Usado para roteamento de transações.',
    example: 'O BIN 4111 11 identifica cartões Visa emitidos pelo banco XYZ.',
  },
  {
    term: 'CVV',
    title: 'Card Verification Value',
    description: 'Código de segurança de 3 ou 4 dígitos impresso no cartão, usado para validar transações online onde o cartão não está fisicamente presente.',
    example: 'Para compras online, é necessário informar o CVV que está no verso do cartão.',
  },
  {
    term: 'Emissor',
    title: 'Instituição Emissora',
    description: 'Instituição financeira responsável por emitir o cartão e conceder o crédito ao portador. Assume o risco de inadimplência do cliente.',
    example: 'O banco UniãoBR é o emissor dos cartões oferecidos aos correntistas.',
  },
  {
    term: 'Bandeira',
    title: 'Bandeira do Cartão',
    description: 'Empresa responsável pela rede de pagamentos que conecta emissores e adquirentes, definindo regras e padrões para transações com cartão.',
    example: 'As principais bandeiras no Brasil são Visa, Mastercard e Elo.',
  },
  {
    term: 'Fatura',
    title: 'Fatura do Cartão',
    description: 'Documento mensal que detalha todas as transações realizadas com o cartão de crédito no período, com valor total a pagar e data de vencimento.',
    example: 'A fatura de janeiro totalizou R$ 3.500 com vencimento em 10/02.',
  },
  {
    term: 'Limite',
    title: 'Limite de Crédito',
    description: 'Valor máximo que o portador pode gastar com o cartão de crédito. É definido pelo emissor com base na análise de crédito do cliente.',
    example: 'O cliente recebeu um limite de R$ 15.000 no cartão de crédito.',
  },
  {
    term: 'Cartão Virtual',
    title: 'Cartão Virtual',
    description: 'Versão digital do cartão com numeração própria, gerada instantaneamente para uso em compras online. Pode ser descartável ou recorrente.',
    example: 'Gere um cartão virtual descartável para compras em sites que você não conhece.',
  },
  {
    term: 'Cartão Físico',
    title: 'Cartão Físico',
    description: 'Cartão plástico com chip e/ou tarja magnética para uso em estabelecimentos físicos. Pode ter tecnologia contactless (NFC) para pagamentos por aproximação.',
    example: 'O cartão físico será entregue em até 10 dias úteis no endereço cadastrado.',
  },
  {
    term: 'Anuidade',
    title: 'Anuidade',
    description: 'Taxa cobrada anualmente pelo emissor pela manutenção do cartão de crédito. Pode ser isenta conforme o perfil do cliente ou programa de relacionamento.',
    example: 'A anuidade do cartão premium é de R$ 600, parcelada em 12x de R$ 50.',
  },
  {
    term: 'Cashback',
    title: 'Cashback',
    description: 'Programa de recompensa que devolve ao cliente um percentual do valor gasto em compras com o cartão, creditado na fatura ou conta corrente.',
    example: 'O cartão oferece 1,5% de cashback em todas as compras, creditado automaticamente na fatura.',
  },
];


/**
 * Glossário de termos de Open Finance
 */
export const openFinanceGlossary: HelpTerm[] = [
  {
    term: 'Consentimento',
    title: 'Consentimento de Compartilhamento',
    description: 'Autorização explícita do cliente para que uma instituição financeira compartilhe seus dados com outra instituição participante do Open Finance.',
    example: 'O cliente concedeu consentimento para compartilhar dados de conta corrente por 12 meses.',
  },
  {
    term: 'FAPI',
    title: 'Financial-grade API',
    description: 'Perfil de segurança avançado baseado no OAuth 2.0, exigido pelo Banco Central para APIs do Open Finance. Garante autenticação e autorização robustas.',
    example: 'Todas as APIs do Open Finance Brasil devem implementar o perfil FAPI para garantir segurança nas transações.',
  },
  {
    term: 'DCR',
    title: 'Dynamic Client Registration',
    description: 'Processo pelo qual uma instituição se registra dinamicamente como cliente de outra instituição no ecossistema Open Finance, usando certificados digitais.',
    example: 'A instituição realizou o DCR no diretório de participantes para acessar as APIs da instituição transmissora.',
  },
  {
    term: 'Compartilhamento de Dados',
    title: 'Compartilhamento de Dados',
    description: 'Transferência de dados financeiros do cliente entre instituições participantes do Open Finance, mediante consentimento. Inclui dados cadastrais, contas, transações e produtos.',
    example: 'Com o compartilhamento de dados, o app pode mostrar saldos de todas as suas contas em diferentes bancos.',
  },
  {
    term: 'Iniciação de Pagamento',
    title: 'Iniciação de Pagamento',
    description: 'Funcionalidade do Open Finance que permite iniciar um pagamento (PIX) a partir de uma instituição diferente daquela onde o cliente tem conta.',
    example: 'Você pode pagar uma compra via PIX iniciado pelo app da loja, debitando diretamente da sua conta no banco.',
  },
  {
    term: 'Instituição Receptora',
    title: 'Instituição Receptora (TPP)',
    description: 'Instituição que recebe os dados compartilhados pelo cliente ou inicia pagamentos em nome dele. Também chamada de Third Party Provider (TPP).',
    example: 'O app de gestão financeira atua como instituição receptora, agregando dados de múltiplos bancos.',
  },
  {
    term: 'Instituição Transmissora',
    title: 'Instituição Transmissora (ASPSP)',
    description: 'Instituição que detém os dados e a conta do cliente, responsável por transmitir as informações mediante consentimento. Também chamada de ASPSP.',
    example: 'O banco onde você tem conta corrente é a instituição transmissora dos seus dados financeiros.',
  },
];


/**
 * Glossário de termos de KYC e Verificação de Identidade
 */
export const kycGlossary: HelpTerm[] = [
  {
    term: 'OCR',
    title: 'Optical Character Recognition',
    description: 'Tecnologia que extrai texto automaticamente de imagens de documentos, como RG, CNH e comprovantes. Acelera o processo de cadastro e verificação.',
    example: 'O OCR extraiu automaticamente nome, CPF e data de nascimento da foto do RG enviada.',
  },
  {
    term: 'Liveness',
    title: 'Detecção de Vivacidade',
    description: 'Tecnologia que verifica se a pessoa na câmera é real e está presente no momento, prevenindo fraudes com fotos, vídeos ou máscaras.',
    example: 'O sistema pediu para o usuário piscar e virar a cabeça para confirmar que é uma pessoa real.',
  },
  {
    term: 'Bureau',
    title: 'Bureau de Crédito',
    description: 'Empresa que coleta e armazena informações de crédito de consumidores e empresas, fornecendo relatórios para análise de risco.',
    example: 'A consulta ao bureau retornou score 720 e nenhuma restrição para o CPF informado.',
  },
  {
    term: 'PEP',
    title: 'Pessoa Exposta Politicamente',
    description: 'Indivíduo que exerce ou exerceu função pública relevante, exigindo monitoramento reforçado conforme regulamentações de PLD/FT.',
    example: 'O sistema identificou o cliente como PEP e ativou o fluxo de due diligence reforçada.',
  },
  {
    term: 'Sanctions',
    title: 'Listas de Sanções',
    description: 'Bases de dados internacionais de pessoas e entidades sancionadas por governos e organismos internacionais, consultadas durante o processo de KYC.',
    example: 'A verificação de sanctions confirmou que o cliente não consta em nenhuma lista restritiva internacional.',
  },
  {
    term: 'Prova de Vida',
    title: 'Prova de Vida',
    description: 'Processo de verificação biométrica que confirma que o usuário está vivo e presente, usando desafios como movimentos faciais ou captura de vídeo.',
    example: 'A prova de vida exigiu que o usuário sorrisse e virasse a cabeça para a direita.',
  },
  {
    term: 'Documento de Identidade',
    title: 'Documento de Identidade',
    description: 'Documento oficial com foto usado para verificação de identidade no processo de KYC. Aceitos: RG, CNH, Passaporte ou RNE.',
    example: 'Envie foto frente e verso do seu RG ou CNH para completar a verificação de identidade.',
  },
  {
    term: 'Comprovante de Residência',
    title: 'Comprovante de Residência',
    description: 'Documento que comprova o endereço atual do cliente, exigido no processo de KYC. Deve ter no máximo 90 dias de emissão.',
    example: 'Conta de luz, água ou telefone dos últimos 3 meses são aceitos como comprovante de residência.',
  },
];


/**
 * Glossário de termos de Investimentos
 */
export const investmentsGlossary: HelpTerm[] = [
  {
    term: 'Suitability',
    title: 'Análise de Perfil do Investidor',
    description: 'Processo de avaliação do perfil de risco do investidor através de questionário, classificando-o como conservador, moderado ou arrojado para recomendar produtos adequados.',
    example: 'O questionário de suitability classificou o cliente como perfil moderado, recomendando CDBs e fundos multimercado.',
  },
  {
    term: 'CDB',
    title: 'Certificado de Depósito Bancário',
    description: 'Título de renda fixa emitido por bancos para captar recursos. O investidor empresta dinheiro ao banco e recebe juros no vencimento. Protegido pelo FGC até R$ 250 mil.',
    example: 'O CDB do banco XYZ rende 110% do CDI com liquidez diária.',
  },
  {
    term: 'LCI',
    title: 'Letra de Crédito Imobiliário',
    description: 'Título de renda fixa emitido por bancos, lastreado em créditos imobiliários. Isento de Imposto de Renda para pessoa física e protegido pelo FGC.',
    example: 'A LCI oferece rentabilidade de 95% do CDI com isenção de IR para pessoa física.',
  },
  {
    term: 'LCA',
    title: 'Letra de Crédito do Agronegócio',
    description: 'Título de renda fixa emitido por bancos, lastreado em créditos do agronegócio. Isento de Imposto de Renda para pessoa física e protegido pelo FGC.',
    example: 'A LCA com vencimento em 2 anos oferece 97% do CDI, isenta de IR.',
  },
  {
    term: 'FGC',
    title: 'Fundo Garantidor de Créditos',
    description: 'Entidade privada que protege depositantes e investidores em caso de falência de instituições financeiras, garantindo até R$ 250 mil por CPF por instituição.',
    example: 'Seus investimentos em CDB, LCI e LCA estão protegidos pelo FGC até R$ 250 mil.',
  },
  {
    term: 'Renda Fixa',
    title: 'Renda Fixa',
    description: 'Categoria de investimentos onde as regras de remuneração são definidas no momento da aplicação. O investidor sabe antecipadamente como será calculado o rendimento.',
    example: 'CDB, LCI, LCA, Tesouro Direto e debêntures são exemplos de investimentos de renda fixa.',
  },
  {
    term: 'Renda Variável',
    title: 'Renda Variável',
    description: 'Categoria de investimentos onde o retorno não é previsível, podendo variar positiva ou negativamente conforme condições de mercado.',
    example: 'Ações, fundos imobiliários e criptomoedas são exemplos de investimentos de renda variável.',
  },
  {
    term: 'Rentabilidade',
    title: 'Rentabilidade',
    description: 'Retorno financeiro obtido em um investimento, expresso em percentual sobre o valor aplicado. Pode ser bruta (antes de impostos) ou líquida (após impostos).',
    example: 'O fundo apresentou rentabilidade de 12,5% nos últimos 12 meses.',
  },
  {
    term: 'Liquidez',
    title: 'Liquidez',
    description: 'Facilidade e velocidade com que um investimento pode ser convertido em dinheiro sem perda significativa de valor. Liquidez diária permite resgate a qualquer momento.',
    example: 'O CDB com liquidez diária permite resgate a qualquer momento, enquanto o com vencimento em 2 anos só pode ser resgatado na data.',
  },
  {
    term: 'Vencimento',
    title: 'Vencimento',
    description: 'Data em que o investimento atinge seu prazo final e o valor aplicado mais os rendimentos são devolvidos ao investidor.',
    example: 'O CDB tem vencimento em 01/01/2028, quando o valor investido mais os juros serão creditados na conta.',
  },
];


/**
 * Glossário de termos de Segurança
 */
export const securityGlossary: HelpTerm[] = [
  {
    term: '2FA',
    title: 'Autenticação de Dois Fatores',
    description: 'Método de segurança que exige duas formas diferentes de verificação para acessar uma conta: algo que você sabe (senha) e algo que você tem (código do celular).',
    example: 'Com 2FA ativado, além da senha você precisa informar o código de 6 dígitos gerado no app autenticador.',
  },
  {
    term: 'Biometria',
    title: 'Autenticação Biométrica',
    description: 'Método de verificação de identidade baseado em características físicas únicas do usuário, como impressão digital, reconhecimento facial ou padrão de voz.',
    example: 'A biometria facial é usada para confirmar a identidade do usuário durante assinaturas de contratos.',
  },
  {
    term: 'Voiceprint',
    title: 'Impressão Vocal',
    description: 'Perfil biométrico único baseado nas características vocais do usuário, usado para autenticação por voz. Analisa frequência, tom, ritmo e padrões de fala.',
    example: 'O sistema de voiceprint reconheceu a voz do usuário e autorizou a transação de alto valor.',
  },
  {
    term: 'Geolocalização',
    title: 'Geolocalização',
    description: 'Tecnologia que identifica a localização geográfica do dispositivo do usuário, usada para validar operações e detectar acessos suspeitos de locais incomuns.',
    example: 'A transação foi bloqueada porque a geolocalização indicou acesso de um país diferente do habitual.',
  },
  {
    term: 'MFA',
    title: 'Multi-Factor Authentication',
    description: 'Autenticação que combina múltiplos fatores de verificação: conhecimento (senha), posse (dispositivo) e inerência (biometria) para máxima segurança.',
    example: 'Para operações acima de R$ 50.000, o sistema exige MFA com senha, código SMS e biometria facial.',
  },
  {
    term: 'Sessão',
    title: 'Sessão de Usuário',
    description: 'Período de tempo em que o usuário permanece autenticado no sistema. Expira automaticamente após inatividade para proteger contra acessos não autorizados.',
    example: 'Sua sessão expira após 30 minutos de inatividade. Faça login novamente para continuar.',
  },
  {
    term: 'Token de Segurança',
    title: 'Token de Segurança',
    description: 'Código temporário gerado por dispositivo ou aplicativo autenticador, usado como segundo fator de autenticação. Válido por curto período (30-60 segundos).',
    example: 'Informe o token de 6 dígitos exibido no seu app autenticador para confirmar o login.',
  },
  {
    term: 'Blockchain de Assinaturas',
    title: 'Blockchain de Assinaturas',
    description: 'Registro imutável de assinaturas digitais em blockchain, garantindo que cada assinatura seja verificável, rastreável e à prova de adulteração.',
    example: 'A assinatura do contrato foi registrada na blockchain, gerando um hash único para verificação futura.',
  },
];


/**
 * Glossário de termos Administrativos
 */
export const adminGlossary: HelpTerm[] = [
  {
    term: 'Tenant',
    title: 'Tenant (Inquilino)',
    description: 'Organização ou empresa que utiliza a plataforma como seu próprio sistema financeiro. Cada tenant tem dados, configurações e usuários isolados dos demais.',
    example: 'O banco digital "FinBank" é um tenant da plataforma, com sua própria marca e configurações.',
  },
  {
    term: 'RBAC',
    title: 'Role-Based Access Control',
    description: 'Sistema de controle de acesso baseado em papéis, onde permissões são atribuídas a roles (Admin, Operator, Compliance, Customer) e não a usuários individuais.',
    example: 'O RBAC garante que apenas usuários com role COMPLIANCE possam aprovar ou rejeitar ativos.',
  },
  {
    term: 'Fee Cascade',
    title: 'Cascata de Taxas',
    description: 'Sistema hierárquico de taxas onde regras de cobrança são definidas em múltiplos níveis (plataforma, tenant, produto), com prioridade do mais específico para o mais geral.',
    example: 'A taxa de PIX é 0,5% na plataforma, mas o tenant configurou 0,3% para seus clientes premium.',
  },
  {
    term: 'Provider',
    title: 'Provider (Provedor)',
    description: 'Serviço externo integrado à plataforma para executar operações específicas como pagamentos, emissão de boletos, verificação de identidade ou interação com blockchain.',
    example: 'O provider OpenPix é usado para processar pagamentos PIX, enquanto o Chainlink fornece dados de oráculos.',
  },
  {
    term: 'Module',
    title: 'Módulo',
    description: 'Funcionalidade da plataforma que pode ser ativada ou desativada por tenant conforme seu plano. Exemplos: PIX, Tokenização, Crédito, Cartões.',
    example: 'O tenant ativou os módulos Banking, PIX e Tokenização no plano Premium.',
  },
  {
    term: 'Tier',
    title: 'Tier (Nível de Plano)',
    description: 'Nível de assinatura do tenant que determina quais módulos estão disponíveis. Tiers: Basic (banking), Standard (+ crédito, contratos) e Premium (+ tokenização, cartões).',
    example: 'Para acessar o módulo de Tokenização, o tenant precisa estar no tier Premium.',
  },
  {
    term: 'White-label',
    title: 'White-label',
    description: 'Modelo de negócio onde a plataforma é oferecida sem marca própria, permitindo que cada tenant aplique sua identidade visual (logo, cores, fontes) como se fosse seu próprio produto.',
    example: 'O tenant "FinBank" usa a plataforma white-label com sua própria marca, cores e domínio personalizado.',
  },
  {
    term: 'Multi-tenant',
    title: 'Multi-tenant (Multi-inquilino)',
    description: 'Arquitetura onde uma única instância da aplicação atende múltiplos tenants com isolamento completo de dados, configurações e personalizações.',
    example: 'A plataforma multi-tenant atende 50 instituições financeiras diferentes, cada uma com seus próprios dados isolados.',
  },
  {
    term: 'Webhook',
    title: 'Webhook',
    description: 'Mecanismo de notificação automática onde o sistema envia dados em tempo real para uma URL configurada quando um evento específico ocorre.',
    example: 'O webhook notifica o sistema do tenant quando um pagamento PIX é confirmado.',
  },
  {
    term: 'API Key',
    title: 'Chave de API',
    description: 'Credencial única usada para autenticar e autorizar o acesso programático aos endpoints da API da plataforma. Deve ser mantida em sigilo.',
    example: 'Use sua API Key no header Authorization para acessar os endpoints de integração.',
  },
];


/**
 * Mapa de termos por rota/página (214+ páginas)
 */
export const pageHelpContent: Record<string, { title: string; description: string; terms: string[] }> = {
  // =============================================
  // SUPER ADMIN (~20 rotas)
  // =============================================
  '/super-admin/dashboard': {
    title: 'Dashboard Global',
    description: 'Visão geral de toda a plataforma com métricas de todos os tenants, transações e módulos ativos.',
    terms: ['Tenant', 'Multi-tenant', 'Module'],
  },
  '/super-admin/tenants': {
    title: 'Gestão de Tenants',
    description: 'Crie, edite e gerencie os tenants (instituições) que utilizam a plataforma.',
    terms: ['Tenant', 'Multi-tenant', 'Tier', 'White-label'],
  },
  '/super-admin/users': {
    title: 'Usuários Globais',
    description: 'Gerencie usuários de qualquer tenant da plataforma com controle de acesso centralizado.',
    terms: ['RBAC', 'Tenant', 'Multi-tenant'],
  },
  '/super-admin/themes': {
    title: 'Editor de Temas',
    description: 'Personalize a identidade visual de cada tenant com cores, logos e fontes.',
    terms: ['White-label', 'Tenant'],
  },
  '/super-admin/theming-advanced': {
    title: 'Editor Avançado de Temas',
    description: 'Configure templates de layout, variantes visuais e estilos avançados para white-label.',
    terms: ['White-label', 'Tenant', 'Tier'],
  },
  '/super-admin/modules': {
    title: 'Módulos da Plataforma',
    description: 'Defina quais módulos estão disponíveis na plataforma e seus requisitos de tier.',
    terms: ['Module', 'Tier', 'Tenant'],
  },
  '/super-admin/asset-types': {
    title: 'Categorias de Tokenização',
    description: 'Gerencie as categorias de ativos disponíveis para tokenização na plataforma.',
    terms: ['RWA', 'Token', 'Compliance'],
  },
  '/super-admin/tokenization': {
    title: 'Dashboard de Tokenização Global',
    description: 'Visão consolidada de todos os ativos tokenizados em todos os tenants.',
    terms: ['RWA', 'Token', 'AUM', 'Supply'],
  },
  '/super-admin/tokens': {
    title: 'Gestão de Tokens',
    description: 'Monitore e gerencie tokens deployados em toda a plataforma.',
    terms: ['Token', 'Deploy', 'Smart Contract', 'ERC-20'],
  },
  '/super-admin/compliance-rules': {
    title: 'Regras de Compliance',
    description: 'Configure regras globais de compliance aplicáveis a todos os tenants.',
    terms: ['Compliance', 'KYC', 'AML', 'PLD/FT'],
  },
  '/super-admin/oracle': {
    title: 'Configuração de Oráculos',
    description: 'Configure oráculos Chainlink para Proof of Reserve e Price Feeds.',
    terms: ['Oracle', 'Proof of Reserve', 'Token'],
  },
  '/super-admin/provider-templates': {
    title: 'Templates de Providers',
    description: 'Gerencie os templates de providers disponíveis para integração pelos tenants.',
    terms: ['Provider', 'Webhook', 'API Key'],
  },
  '/super-admin/providers': {
    title: 'Gestão de Providers',
    description: 'Visualize e gerencie todos os providers configurados na plataforma.',
    terms: ['Provider', 'API Key', 'Webhook'],
  },
  '/super-admin/costs': {
    title: 'Custos Operacionais',
    description: 'Monitore os custos operacionais de providers e infraestrutura da plataforma.',
    terms: ['Provider', 'Fee Cascade', 'Tenant'],
  },
  '/super-admin/fee-config': {
    title: 'Taxas da Plataforma',
    description: 'Configure as taxas globais cobradas pela plataforma aos tenants.',
    terms: ['Fee Cascade', 'Tenant', 'Tier'],
  },
  '/super-admin/fee-health': {
    title: 'Saúde das Taxas',
    description: 'Verifique a consistência e saúde das configurações de taxas em toda a plataforma.',
    terms: ['Fee Cascade', 'Tenant'],
  },
  '/super-admin/tenant-revenue': {
    title: 'Receita por Tenant',
    description: 'Acompanhe a receita gerada por cada tenant da plataforma.',
    terms: ['Tenant', 'Fee Cascade', 'Tier'],
  },
  '/super-admin/financial-health': {
    title: 'Saúde Financeira',
    description: 'Dashboard de saúde financeira da plataforma com indicadores de cobertura e risco.',
    terms: ['AUM', 'Proof of Reserve', 'Custody'],
  },
  '/super-admin/financial-security': {
    title: 'Segurança Financeira',
    description: 'Configurações de segurança financeira e políticas de proteção da plataforma.',
    terms: ['Compliance', 'AML', 'PLD/FT'],
  },
  '/super-admin/futuristic-layouts': {
    title: 'Layouts Futurísticos',
    description: 'Galeria de layouts futurísticos disponíveis para personalização dos tenants.',
    terms: ['White-label', 'Tenant'],
  },
  '/super-admin/immersive-themes': {
    title: 'Temas Imersivos',
    description: 'Galeria de temas imersivos com estilos visuais únicos para white-label.',
    terms: ['White-label', 'Tenant'],
  },

  // =============================================
  // ADMIN GERAL (~17 rotas)
  // =============================================
  '/admin/dashboard': {
    title: 'Dashboard do Tenant',
    description: 'Visão geral do seu tenant com métricas de contas, transações e módulos ativos.',
    terms: ['Tenant', 'Module', 'RBAC'],
  },
  '/admin/setup': {
    title: 'Setup Wizard',
    description: 'Assistente de configuração inicial do tenant com passos guiados.',
    terms: ['Tenant', 'Provider', 'Module'],
  },
  '/admin/users': {
    title: 'Gestão de Usuários',
    description: 'Crie e gerencie os usuários do seu tenant com diferentes papéis e permissões.',
    terms: ['RBAC', 'Tenant'],
  },
  '/admin/entities': {
    title: 'Entidades',
    description: 'Gerencie as entidades (pessoas físicas e jurídicas) cadastradas no tenant.',
    terms: ['KYC', 'Tenant'],
  },
  '/admin/bank-connections': {
    title: 'Conexões Bancárias',
    description: 'Configure conexões com instituições bancárias para operações financeiras.',
    terms: ['Provider', 'Open Finance', 'PIX'],
  },
  '/admin/provider-templates': {
    title: 'Templates de Providers',
    description: 'Visualize os templates de providers disponíveis para configuração.',
    terms: ['Provider', 'API Key', 'Webhook'],
  },
  '/admin/provider-instances': {
    title: 'Instâncias de Providers',
    description: 'Gerencie as instâncias de providers configuradas para o seu tenant.',
    terms: ['Provider', 'API Key', 'Webhook'],
  },
  '/admin/test-console': {
    title: 'Console de Testes',
    description: 'Teste as integrações com providers em ambiente sandbox.',
    terms: ['Provider', 'API Key'],
  },
  '/admin/monitoring': {
    title: 'Monitoramento',
    description: 'Monitore o status e a saúde dos providers e serviços integrados.',
    terms: ['Provider', 'Webhook'],
  },
  '/admin/fee-rules': {
    title: 'Regras de Taxa',
    description: 'Configure as regras de cobrança de taxas para os clientes do tenant.',
    terms: ['Fee Cascade', 'Tenant', 'Tier'],
  },
  '/admin/fee-config': {
    title: 'Configuração de Taxas',
    description: 'Defina os valores e condições das taxas cobradas pelo tenant.',
    terms: ['Fee Cascade', 'Tenant'],
  },
  '/admin/revenue': {
    title: 'Dashboard de Receita',
    description: 'Acompanhe a receita gerada pelas taxas e operações do tenant.',
    terms: ['Fee Cascade', 'Tenant'],
  },
  '/admin/modules': {
    title: 'Módulos do Tenant',
    description: 'Ative ou desative módulos disponíveis conforme o plano do tenant.',
    terms: ['Module', 'Tier', 'Tenant'],
  },
  '/admin/audit': {
    title: 'Logs de Auditoria',
    description: 'Visualize o histórico completo de ações realizadas no sistema.',
    terms: ['RBAC', 'Compliance', 'Tenant'],
  },
  '/admin/permissions': {
    title: 'Permissões',
    description: 'Gerencie as permissões e papéis de acesso dos usuários do tenant.',
    terms: ['RBAC', 'Tenant'],
  },
  '/admin/economic-groups': {
    title: 'Grupos Econômicos',
    description: 'Gerencie grupos econômicos e suas relações entre entidades.',
    terms: ['Tenant', 'KYC', 'Compliance'],
  },
  '/admin/system-health': {
    title: 'Saúde do Sistema',
    description: 'Monitore a saúde geral do sistema, incluindo banco de dados, módulos e serviços.',
    terms: ['Provider', 'Module', 'Tenant'],
  },

  // =============================================
  // ADMIN TOKENIZAÇÃO (~10 rotas)
  // =============================================
  '/admin/tokenization': {
    title: 'Dashboard de Tokenização',
    description: 'Visão geral dos seus ativos tokenizados, incluindo métricas de supply, holders e rendimentos.',
    terms: ['RWA', 'Token', 'Supply', 'Yield', 'AUM'],
  },
  '/admin/tokenization/assets': {
    title: 'Lista de Ativos',
    description: 'Visualize e gerencie todos os ativos tokenizados do tenant.',
    terms: ['RWA', 'Token', 'Compliance'],
  },
  '/admin/tokenization/assets/new': {
    title: 'Novo Ativo',
    description: 'Cadastre um novo ativo para tokenização com documentação e parâmetros.',
    terms: ['RWA', 'Token', 'Custody', 'Compliance'],
  },
  '/admin/tokenization/supply': {
    title: 'Gestão de Supply',
    description: 'Controle a emissão (mint) e queima (burn) de tokens, mantendo o lastro adequado com os ativos em custódia.',
    terms: ['Supply', 'Mint', 'Burn', 'Proof of Reserve', 'Custody'],
  },
  '/admin/tokenization/yield': {
    title: 'Configuração de Yield',
    description: 'Configure como os rendimentos serão calculados e distribuídos aos holders dos tokens.',
    terms: ['Yield', 'Holder', 'Smart Contract', 'Token'],
  },
  '/admin/tokenization/compliance': {
    title: 'Compliance de Tokenização',
    description: 'Gerencie as regras de conformidade, whitelist de investidores e verificações KYC.',
    terms: ['Compliance', 'Whitelist', 'KYC', 'AML'],
  },
  '/admin/tokenization/token-config': {
    title: 'Configuração de Token',
    description: 'Configure os parâmetros do token como nome, símbolo, decimais e padrão (ERC-20, ERC-1400).',
    terms: ['Token', 'ERC-20', 'ERC-1400', 'Smart Contract'],
  },
  '/admin/tokenization/contracts': {
    title: 'Smart Contracts',
    description: 'Gerencie os templates de contratos inteligentes usados para tokenização.',
    terms: ['Smart Contract', 'Deploy', 'ERC-20', 'ERC-1400'],
  },
  '/admin/tokenization/audits': {
    title: 'Auditorias',
    description: 'Visualize o histórico de auditorias de smart contracts e ativos tokenizados.',
    terms: ['Smart Contract', 'Compliance', 'Deploy'],
  },
  '/admin/tokenization/financial-health': {
    title: 'Saúde Financeira',
    description: 'Monitore indicadores de saúde financeira dos ativos tokenizados.',
    terms: ['AUM', 'Proof of Reserve', 'Custody', 'Token'],
  },
  '/admin/tokenization/security': {
    title: 'Segurança de Tokenização',
    description: 'Configure políticas de segurança e visualize logs de operações de tokenização.',
    terms: ['Compliance', 'Smart Contract', 'Mint', 'Burn'],
  },

  // =============================================
  // ADMIN COMPLIANCE (~9 rotas)
  // =============================================
  '/admin/compliance/queue': {
    title: 'Fila de Aprovação',
    description: 'Revise e aprove ativos pendentes de compliance para tokenização.',
    terms: ['Compliance', 'KYC', 'Whitelist'],
  },
  '/admin/compliance/pld': {
    title: 'Dashboard PLD',
    description: 'Painel de Prevenção à Lavagem de Dinheiro com alertas e indicadores.',
    terms: ['PLD/FT', 'COAF', 'Compliance'],
  },
  '/admin/compliance/pld/alerts': {
    title: 'Alertas PLD',
    description: 'Visualize e gerencie alertas de operações suspeitas detectadas pelo sistema.',
    terms: ['PLD/FT', 'Estruturação', 'COAF'],
  },
  '/admin/compliance/pld/rules': {
    title: 'Regras PLD',
    description: 'Configure as regras de detecção de operações suspeitas e limites de monitoramento.',
    terms: ['PLD/FT', 'Estruturação', 'Compliance'],
  },
  '/admin/compliance/pld/config': {
    title: 'Configuração PLD',
    description: 'Defina parâmetros gerais do módulo de PLD/FT do tenant.',
    terms: ['PLD/FT', 'COAF', 'Compliance'],
  },
  '/admin/compliance/coaf': {
    title: 'Dashboard COAF',
    description: 'Gerencie comunicações ao COAF e acompanhe o status de envios.',
    terms: ['COAF', 'ROS', 'DOF', 'PLD/FT'],
  },
  '/admin/compliance/coaf/communications': {
    title: 'Comunicações COAF',
    description: 'Visualize e crie comunicações de operações suspeitas ao COAF.',
    terms: ['COAF', 'ROS', 'DOF'],
  },
  '/admin/compliance/med/queue': {
    title: 'Fila MED',
    description: 'Gerencie solicitações de devolução PIX via Mecanismo Especial de Devolução.',
    terms: ['MED', 'PIX', 'Compliance'],
  },
  '/admin/compliance/med/review': {
    title: 'Revisão MED',
    description: 'Revise e processe solicitações de devolução PIX pendentes.',
    terms: ['MED', 'PIX', 'Compliance'],
  },

  // =============================================
  // ADMIN CONTRATOS (~4 rotas)
  // =============================================
  '/admin/contracts': {
    title: 'Lista de Contratos',
    description: 'Visualize e gerencie todos os contratos digitais do tenant.',
    terms: ['Compliance', 'Tenant'],
  },
  '/admin/contracts/create': {
    title: 'Criar Contrato',
    description: 'Crie um novo contrato digital a partir de um template ou do zero.',
    terms: ['Compliance', 'Tenant'],
  },
  '/admin/contracts/templates': {
    title: 'Templates de Contratos',
    description: 'Gerencie templates de contrato reutilizáveis com variáveis dinâmicas.',
    terms: ['Tenant'],
  },
  '/admin/contracts/dashboard': {
    title: 'Dashboard de Contratos',
    description: 'Métricas e indicadores dos contratos digitais do tenant.',
    terms: ['Tenant', 'Compliance'],
  },

  // =============================================
  // ADMIN KYC (~4 rotas)
  // =============================================
  '/admin/kyc': {
    title: 'Dashboard KYC',
    description: 'Painel de verificação de identidade com métricas de aprovação e pendências.',
    terms: ['KYC', 'Liveness', 'Bureau', 'OCR'],
  },
  '/admin/kyc/queue': {
    title: 'Fila de KYC',
    description: 'Revise e aprove solicitações de verificação de identidade pendentes.',
    terms: ['KYC', 'OCR', 'Prova de Vida', 'Bureau'],
  },
  '/admin/kyc/settings': {
    title: 'Configurações KYC',
    description: 'Configure os parâmetros e regras do processo de verificação de identidade.',
    terms: ['KYC', 'Liveness', 'OCR'],
  },
  '/admin/kyc/analysts': {
    title: 'Gestão de Analistas',
    description: 'Gerencie os analistas responsáveis pela revisão de KYC.',
    terms: ['KYC', 'RBAC'],
  },

  // =============================================
  // ADMIN CRÉDITO (~4 rotas)
  // =============================================
  '/admin/credit': {
    title: 'Dashboard de Crédito',
    description: 'Visão geral do motor de crédito com métricas de empréstimos e inadimplência.',
    terms: ['Score', 'Inadimplência', 'CET'],
  },
  '/admin/credit/policies': {
    title: 'Políticas de Crédito',
    description: 'Configure as políticas de aprovação e limites de crédito do tenant.',
    terms: ['Score', 'SCR', 'Garantia'],
  },
  '/admin/credit/products': {
    title: 'Produtos de Crédito',
    description: 'Gerencie os produtos de crédito oferecidos aos clientes.',
    terms: ['CET', 'SAC', 'Price', 'IOF'],
  },
  '/admin/credit/loans': {
    title: 'Empréstimos Ativos',
    description: 'Acompanhe os empréstimos ativos, parcelas e status de pagamento.',
    terms: ['Parcela', 'Inadimplência', 'Score'],
  },

  // =============================================
  // ADMIN CARTÕES, INVESTIMENTOS, OPEN FINANCE, BENEFÍCIOS, SEGURANÇA (~7 rotas)
  // =============================================
  '/admin/cards': {
    title: 'Configuração de Cartões',
    description: 'Configure a emissão de cartões virtuais e físicos para os clientes.',
    terms: ['BIN', 'Emissor', 'Bandeira', 'Limite'],
  },
  '/admin/investments': {
    title: 'Configuração de Investimentos',
    description: 'Gerencie os produtos de investimento disponíveis no marketplace.',
    terms: ['Suitability', 'Renda Fixa', 'CDB', 'FGC'],
  },
  '/admin/open-finance': {
    title: 'Configuração Open Finance',
    description: 'Configure a integração com o ecossistema Open Finance Brasil.',
    terms: ['Open Finance', 'Consentimento', 'FAPI'],
  },
  '/admin/benefits': {
    title: 'Planos de Benefícios',
    description: 'Gerencie os planos de benefícios corporativos oferecidos pelo tenant.',
    terms: ['Tenant', 'Module'],
  },
  '/admin/security': {
    title: 'Configuração de Segurança Avançada',
    description: 'Configure recursos avançados de segurança como biometria e MFA.',
    terms: ['2FA', 'Biometria', 'MFA', 'Voiceprint'],
  },
  '/admin/facial-verification': {
    title: 'Regras de Verificação Facial',
    description: 'Configure as regras e parâmetros da verificação facial biométrica.',
    terms: ['Biometria', 'Liveness', 'Prova de Vida'],
  },
  '/admin/security-audit': {
    title: 'Logs de Segurança',
    description: 'Visualize logs de eventos de segurança e tentativas de acesso.',
    terms: ['2FA', 'MFA', 'Sessão', 'Geolocalização'],
  },

  // =============================================
  // BANKING/CUSTOMER CORE (~10 rotas)
  // =============================================
  '/banking/dashboard': {
    title: 'Dashboard',
    description: 'Visão geral da sua conta com saldo, transações recentes e atalhos rápidos.',
    terms: ['PIX', 'Open Finance'],
  },
  '/banking/statement': {
    title: 'Extrato',
    description: 'Consulte o extrato completo da sua conta com filtros por período e tipo.',
    terms: ['PIX', 'Open Finance'],
  },
  '/banking/pix': {
    title: 'PIX',
    description: 'Realize transferências instantâneas via PIX usando chave, QR Code ou dados bancários.',
    terms: ['PIX', 'Open Finance'],
  },
  '/banking/pix-saque': {
    title: 'PIX Saque',
    description: 'Realize saques em dinheiro usando PIX em estabelecimentos parceiros.',
    terms: ['PIX'],
  },
  '/banking/transfer': {
    title: 'Transferências',
    description: 'Realize transferências bancárias via TED ou entre contas da plataforma.',
    terms: ['PIX', 'Open Finance'],
  },
  '/banking/batch-transfer': {
    title: 'Transferências em Lote',
    description: 'Envie múltiplas transferências de uma vez usando arquivo ou lista.',
    terms: ['PIX'],
  },
  '/banking/recurring-transfers': {
    title: 'Transferências Recorrentes',
    description: 'Configure transferências automáticas com frequência programada.',
    terms: ['PIX'],
  },
  '/banking/bills': {
    title: 'Pagamento de Contas',
    description: 'Pague boletos, contas de consumo e tributos diretamente pela plataforma.',
    terms: ['PIX'],
  },
  '/banking/mobile-topup': {
    title: 'Recarga de Celular',
    description: 'Faça recargas de celular para qualquer operadora diretamente da sua conta.',
    terms: ['PIX'],
  },
  '/banking/scheduled-pix': {
    title: 'PIX Agendado',
    description: 'Agende transferências PIX para datas futuras com controle de recorrência.',
    terms: ['PIX'],
  },

  // =============================================
  // BANKING TOKENS (~4 rotas)
  // =============================================
  '/banking/tokens': {
    title: 'Marketplace de Tokens',
    description: 'Explore e invista em ativos tokenizados disponíveis na plataforma.',
    terms: ['RWA', 'Token', 'Yield', 'Holder'],
  },
  '/banking/portfolio': {
    title: 'Portfólio de Tokens',
    description: 'Visualize seus tokens, rendimentos acumulados e histórico de operações.',
    terms: ['Token', 'Yield', 'Holder', 'AUM'],
  },
  '/banking/orders': {
    title: 'Histórico de Ordens',
    description: 'Acompanhe suas ordens de compra e venda de tokens.',
    terms: ['Token', 'Holder'],
  },
  '/banking/yields': {
    title: 'Histórico de Rendimentos',
    description: 'Visualize os rendimentos recebidos dos seus tokens ao longo do tempo.',
    terms: ['Yield', 'Token', 'Holder'],
  },

  // =============================================
  // BANKING CONTRATOS, CRÉDITO, CARTÕES (~12 rotas)
  // =============================================
  '/banking/contracts': {
    title: 'Meus Contratos',
    description: 'Visualize e assine contratos digitais pendentes e concluídos.',
    terms: ['Compliance'],
  },
  '/banking/credit/simulator': {
    title: 'Simulador de Crédito',
    description: 'Simule empréstimos com diferentes valores, prazos e taxas.',
    terms: ['CET', 'SAC', 'Price', 'IOF', 'Parcela'],
  },
  '/banking/credit/loans': {
    title: 'Meus Empréstimos',
    description: 'Acompanhe seus empréstimos ativos, parcelas e pagamentos.',
    terms: ['Parcela', 'Inadimplência', 'CET'],
  },
  '/banking/credit/request': {
    title: 'Solicitar Empréstimo',
    description: 'Solicite um novo empréstimo com análise de crédito automática.',
    terms: ['Score', 'CET', 'Parcela', 'Garantia'],
  },
  '/banking/cards': {
    title: 'Meus Cartões',
    description: 'Gerencie seus cartões virtuais e físicos, limites e configurações.',
    terms: ['Cartão Virtual', 'Limite', 'Fatura', 'CVV'],
  },
  '/banking/cards/request': {
    title: 'Solicitar Cartão',
    description: 'Solicite um novo cartão virtual ou físico para sua conta.',
    terms: ['Cartão Virtual', 'BIN', 'Bandeira', 'Limite'],
  },
  '/banking/investments': {
    title: 'Marketplace de Investimentos',
    description: 'Explore produtos de investimento disponíveis como CDB, LCI e LCA.',
    terms: ['Suitability', 'CDB', 'LCI', 'LCA', 'Renda Fixa'],
  },
  '/banking/investments/portfolio': {
    title: 'Meu Portfólio',
    description: 'Acompanhe seus investimentos, rentabilidade e vencimentos.',
    terms: ['Rentabilidade', 'Liquidez', 'FGC'],
  },
  '/banking/investments/simulator': {
    title: 'Simulador de Investimentos',
    description: 'Simule investimentos com diferentes valores e prazos.',
    terms: ['Rentabilidade', 'CDB', 'Renda Fixa', 'Liquidez'],
  },
  '/banking/benefits': {
    title: 'Catálogo de Benefícios',
    description: 'Explore os benefícios corporativos disponíveis para adesão.',
    terms: ['Cashback'],
  },
  '/banking/subscriptions': {
    title: 'Minhas Adesões',
    description: 'Gerencie suas adesões a planos de benefícios corporativos.',
    terms: ['Cashback'],
  },
  '/banking/open-finance': {
    title: 'Consentimentos Open Finance',
    description: 'Gerencie seus consentimentos de compartilhamento de dados financeiros.',
    terms: ['Open Finance', 'Consentimento', 'Compartilhamento de Dados'],
  },

  // =============================================
  // BANKING CRYPTO/DEFI (~11 rotas)
  // =============================================
  '/banking/crypto': {
    title: 'Dashboard Crypto',
    description: 'Visão geral das suas criptomoedas, carteiras e operações.',
    terms: ['Wallet', 'Swap', 'Gas'],
  },
  '/banking/crypto/wallets': {
    title: 'Carteiras',
    description: 'Gerencie suas carteiras de criptomoedas e tokens.',
    terms: ['Wallet', 'Gas', 'Token'],
  },
  '/banking/crypto/marketplace': {
    title: 'Marketplace P2P',
    description: 'Compre e venda criptomoedas diretamente com outros usuários.',
    terms: ['Wallet', 'Swap'],
  },
  '/banking/crypto/history': {
    title: 'Histórico Crypto',
    description: 'Visualize o histórico completo de operações com criptomoedas.',
    terms: ['Wallet', 'Swap', 'Gas'],
  },
  '/banking/defi/pools': {
    title: 'Pools de Liquidez',
    description: 'Forneça liquidez para pools e ganhe taxas das transações.',
    terms: ['Liquidity Pool', 'DEX', 'Swap', 'Yield Farming'],
  },
  '/banking/defi/positions': {
    title: 'Posições DeFi',
    description: 'Acompanhe suas posições ativas em protocolos DeFi.',
    terms: ['Liquidity Pool', 'Staking', 'TVL'],
  },
  '/banking/staking': {
    title: 'Staking',
    description: 'Bloqueie seus tokens para receber recompensas passivas.',
    terms: ['Staking', 'Yield', 'Token', 'TVL'],
  },
  '/banking/yield-farming': {
    title: 'Yield Farming',
    description: 'Maximize seus rendimentos movendo ativos entre protocolos DeFi.',
    terms: ['Yield Farming', 'Liquidity Pool', 'DEX'],
  },
  '/banking/bridge': {
    title: 'Bridge',
    description: 'Transfira ativos entre diferentes blockchains.',
    terms: ['Bridge', 'Gas', 'Wallet'],
  },
  '/banking/advanced-swap': {
    title: 'Swap Avançado',
    description: 'Troque tokens com roteamento otimizado entre múltiplas DEXs.',
    terms: ['Swap', 'DEX', 'Gas', 'Liquidity Pool'],
  },
  '/banking/nft': {
    title: 'Galeria NFT',
    description: 'Visualize e gerencie seus NFTs e colecionáveis digitais.',
    terms: ['Wallet', 'Token', 'Smart Contract'],
  },

  // =============================================
  // BANKING KYC E CONFIGURAÇÕES (~7 rotas)
  // =============================================
  '/banking/kyc': {
    title: 'Submissão KYC',
    description: 'Envie seus documentos para verificação de identidade.',
    terms: ['KYC', 'OCR', 'Liveness', 'Prova de Vida'],
  },
  '/banking/kyc/status': {
    title: 'Status KYC',
    description: 'Acompanhe o status da sua verificação de identidade.',
    terms: ['KYC', 'Bureau'],
  },
  '/banking/identity-verification': {
    title: 'Verificação de Identidade',
    description: 'Complete a verificação biométrica facial para confirmar sua identidade.',
    terms: ['Biometria', 'Liveness', 'Prova de Vida'],
  },
  '/banking/settings/account': {
    title: 'Configurações da Conta',
    description: 'Gerencie seus dados pessoais e preferências de conta.',
    terms: ['KYC', 'Tenant'],
  },
  '/banking/settings/security': {
    title: 'Configurações de Segurança',
    description: 'Configure autenticação de dois fatores e outras opções de segurança.',
    terms: ['2FA', 'MFA', 'Biometria', 'Sessão'],
  },
  '/banking/settings/2fa': {
    title: 'Autenticação 2FA',
    description: 'Configure a autenticação de dois fatores para proteger sua conta.',
    terms: ['2FA', 'MFA', 'Token de Segurança'],
  },
  '/banking/settings/notifications': {
    title: 'Notificações',
    description: 'Configure suas preferências de notificações por e-mail, SMS e push.',
    terms: ['Webhook'],
  },

  // =============================================
  // BANKING MED (~2 rotas)
  // =============================================
  '/banking/med': {
    title: 'Solicitações MED',
    description: 'Visualize suas solicitações de devolução PIX via MED.',
    terms: ['MED', 'PIX'],
  },
  '/banking/med/create': {
    title: 'Nova Solicitação MED',
    description: 'Solicite a devolução de um PIX recebido indevidamente ou por fraude.',
    terms: ['MED', 'PIX', 'Compliance'],
  },
};


/**
 * Tipo de categoria do glossário
 */
export type GlossaryCategory =
  | 'tokenization' | 'defi' | 'banking'
  | 'compliance' | 'credit' | 'cards'
  | 'open_finance' | 'kyc' | 'investments'
  | 'security' | 'admin';

/**
 * Mapa de categorias para arrays de termos
 */
const glossaryCategoryMap: Record<GlossaryCategory, HelpTerm[]> = {
  tokenization: tokenizationGlossary,
  defi: defiGlossary,
  banking: bankingGlossary,
  compliance: complianceGlossary,
  credit: creditGlossary,
  cards: cardsGlossary,
  open_finance: openFinanceGlossary,
  kyc: kycGlossary,
  investments: investmentsGlossary,
  security: securityGlossary,
  admin: adminGlossary,
};

/**
 * Busca um termo no glossário (todas as categorias)
 */
export function findTerm(term: string): HelpTerm | undefined {
  const allTerms = [
    ...tokenizationGlossary, ...defiGlossary, ...bankingGlossary,
    ...complianceGlossary, ...creditGlossary, ...cardsGlossary,
    ...openFinanceGlossary, ...kycGlossary, ...investmentsGlossary,
    ...securityGlossary, ...adminGlossary,
  ];
  return allTerms.find(t => t.term.toLowerCase() === term.toLowerCase());
}

/**
 * Busca termos por categoria
 */
export function getTermsByCategory(category: GlossaryCategory): HelpTerm[] {
  return glossaryCategoryMap[category] || [];
}

/**
 * Obtém o conteúdo de ajuda para uma página específica.
 * Suporta match exato e fallback por prefixo mais longo.
 */
export function getPageHelp(pathname: string): { title: string; description: string; terms: HelpTerm[] } | undefined {
  // Primeiro tenta match exato
  const exact = pageHelpContent[pathname];
  if (exact) {
    const terms = exact.terms.map(t => findTerm(t)).filter(Boolean) as HelpTerm[];
    return { title: exact.title, description: exact.description, terms };
  }

  // Fallback: match por prefixo mais longo
  const prefixes = Object.keys(pageHelpContent)
    .filter(k => pathname.startsWith(k))
    .sort((a, b) => b.length - a.length);

  if (prefixes.length > 0) {
    const match = pageHelpContent[prefixes[0]];
    const terms = match.terms.map(t => findTerm(t)).filter(Boolean) as HelpTerm[];
    return { title: match.title, description: match.description, terms };
  }

  return undefined;
}


/**
 * Conteúdo de ajuda para o módulo de Contratos Digitais
 */
export const contractsHelp = {
  create: {
    helpContent: `
## Criar Novo Contrato

Crie contratos a partir de templates existentes em 3 passos simples.

### Passo a Passo

1. **Selecione um Template**: Escolha o modelo de contrato adequado
2. **Preencha as Variáveis**: Complete os campos dinâmicos do contrato
3. **Preview e Envio**: Revise o contrato e envie para assinatura

### Dicas

- Você pode vincular o contrato a um ativo tokenizado para preencher automaticamente dados do ativo
- Use o modo "Dados Reais" no preview para ver como o contrato ficará com dados do sistema
- Campos obrigatórios são marcados com asterisco vermelho
    `,
  },
  dashboard: {
    helpContent: `
## Contratos Digitais

O módulo de Contratos Digitais permite criar, gerenciar e assinar contratos eletrônicos com validade jurídica.

### Funcionalidades Principais

- **Templates**: Crie modelos de contrato reutilizáveis com variáveis dinâmicas
- **Assinatura Eletrônica**: Coleta de assinaturas com verificação biométrica facial
- **Rastreabilidade**: Histórico completo de todas as ações no contrato
- **Integração**: Conexão com tokenização para contratos de investimento

### Status dos Contratos

- **Rascunho**: Contrato em edição, ainda não enviado
- **Aguardando Assinatura**: Enviado para os signatários
- **Parcialmente Assinado**: Alguns signatários já assinaram
- **Concluído**: Todas as assinaturas coletadas
- **Expirado**: Prazo de assinatura vencido
- **Cancelado**: Contrato cancelado pelo emissor

### Validade Jurídica

Os contratos assinados na plataforma têm validade jurídica conforme:
- MP 2.200-2/2001 (ICP-Brasil)
- Lei 14.063/2020 (Assinaturas Eletrônicas)
- Código Civil Brasileiro
    `,
  },
  templates: {
    helpContent: `
## Templates de Contrato

Templates são modelos de contrato que podem ser reutilizados para criar novos contratos rapidamente.

### Variáveis Dinâmicas

Use variáveis no formato \`{{nome_variavel}}\` para campos que serão preenchidos automaticamente:

- \`{{nome_completo}}\` - Nome do signatário
- \`{{cpf}}\` - CPF do signatário
- \`{{data_atual}}\` - Data de criação do contrato
- \`{{valor}}\` - Valor do contrato

### Categorias de Templates

- **Investimento**: Contratos de adesão a tokens e ativos
- **Prestação de Serviços**: Contratos de serviço
- **Termos de Uso**: Termos e condições
- **Procuração**: Documentos de representação
    `,
  },
  signing: {
    helpContent: `
## Processo de Assinatura

### Etapas da Assinatura

1. **Identificação**: Verificação dos dados do signatário
2. **Leitura**: Visualização completa do contrato
3. **Verificação Facial**: Captura de selfie para validação biométrica
4. **Assinatura**: Desenho da assinatura manuscrita digital
5. **Confirmação**: Revisão e confirmação final

### Segurança

- Verificação biométrica facial obrigatória
- Captura de geolocalização
- Registro de IP e dispositivo
- Hash criptográfico do documento
    `,
  },
  faqs: [
    {
      question: 'Os contratos assinados digitalmente têm validade jurídica?',
      answer: 'Sim. Os contratos assinados na plataforma têm validade jurídica conforme a MP 2.200-2/2001 e a Lei 14.063/2020, que regulamentam as assinaturas eletrônicas no Brasil.',
    },
    {
      question: 'Como funciona a verificação biométrica?',
      answer: 'Durante a assinatura, o signatário deve tirar uma selfie que é comparada com a foto do documento de identidade cadastrado. Isso garante que a pessoa que está assinando é realmente quem diz ser.',
    },
    {
      question: 'Posso cancelar um contrato após enviá-lo?',
      answer: 'Sim, contratos podem ser cancelados enquanto não tiverem sido completamente assinados. Após todas as assinaturas serem coletadas, o contrato não pode mais ser cancelado.',
    },
    {
      question: 'Como os signatários recebem o contrato?',
      answer: 'Os signatários recebem um e-mail com link para acessar e assinar o contrato. Também podem acessar através do app ou portal do cliente.',
    },
    {
      question: 'Quanto tempo o signatário tem para assinar?',
      answer: 'O prazo de assinatura é configurável ao criar o contrato. O padrão é 7 dias, mas pode ser ajustado conforme necessidade.',
    },
    {
      question: 'Como verificar a autenticidade de um contrato assinado?',
      answer: 'Cada contrato possui um QR Code único que permite verificar sua autenticidade. Ao escanear, você acessa os dados do contrato e pode confirmar as assinaturas.',
    },
    {
      question: 'Posso usar meus próprios templates de contrato?',
      answer: 'Sim. Você pode criar templates personalizados com seu próprio texto e variáveis dinâmicas, ou usar os templates pré-configurados da plataforma.',
    },
    {
      question: 'O que acontece se um signatário não assinar no prazo?',
      answer: 'O contrato expira automaticamente e o status muda para "Expirado". Você pode criar um novo contrato ou estender o prazo antes da expiração.',
    },
  ],
};



/**
 * Conteúdo de ajuda para o módulo de Tokenização
 */
export const tokenizationHelp = {
  supply: {
    helpContent: `
## Gestão de Supply

O Supply (fornecimento) representa a quantidade total de tokens existentes para um ativo tokenizado.

### Tipos de Supply

- **Supply Atual**: Total de tokens emitidos até o momento
- **Supply Máximo**: Limite máximo de tokens que podem ser emitidos
- **Supply Circulante**: Tokens em posse de investidores
- **Supply Bloqueado**: Tokens em lock-up ou reserva
- **Supply Queimado**: Tokens destruídos permanentemente

### Operações

- **Mint**: Emitir novos tokens (requer lastro em custódia)
- **Burn**: Destruir tokens (reduz o supply circulante)
- **Lock**: Bloquear tokens temporariamente
- **Unlock**: Desbloquear tokens previamente bloqueados
    `,
  },
  supplyManagement: {
    helpContent: `
## Gestão de Supply

Controle a emissão e queima de tokens mantendo o lastro adequado.

### Operações Disponíveis

- **Mint**: Emitir novos tokens (requer custódia verificada)
- **Burn**: Destruir tokens permanentemente
- **Lock/Unlock**: Bloquear ou liberar tokens

### Regras de Negócio

- Mint só é permitido se houver custódia verificada
- Burn requer saldo suficiente
- Operações acima do threshold requerem aprovação
    `,
  },
  yield: {
    helpContent: `
## Configuração de Yield

Yield é o rendimento distribuído aos detentores de tokens.

### Tipos de Yield

- **Fixo**: Taxa fixa definida no momento da emissão
- **Variável**: Baseado no desempenho do ativo
- **Híbrido**: Combinação de fixo + variável

### Frequência de Distribuição

- Mensal
- Trimestral
- Semestral
- Anual
- No vencimento
    `,
  },
  compliance: {
    helpContent: `
## Compliance de Tokenização

Regras e verificações para garantir conformidade regulatória.

### Verificações

- **KYC**: Verificação de identidade do investidor
- **Investidor Qualificado**: Validação de perfil
- **Limites de Investimento**: Valores máximos por investidor
- **Restrições Geográficas**: Países permitidos/bloqueados
    `,
  },
  faqs: [
    {
      question: 'O que é tokenização de ativos?',
      answer: 'Tokenização é o processo de representar ativos do mundo real (imóveis, recebíveis, etc.) como tokens digitais na blockchain, permitindo fracionamento e maior liquidez.',
    },
    {
      question: 'Como funciona o Proof of Reserve?',
      answer: 'O Proof of Reserve é um mecanismo que comprova que os tokens emitidos são lastreados por ativos reais em custódia. Oráculos verificam periodicamente o lastro.',
    },
    {
      question: 'Posso emitir mais tokens depois do deploy?',
      answer: 'Depende da política de supply configurada. Se "Permitir Mint Adicional" estiver habilitado, novos tokens podem ser emitidos desde que haja lastro suficiente.',
    },
    {
      question: 'O que acontece quando um token é queimado?',
      answer: 'Tokens queimados são destruídos permanentemente, reduzindo o supply circulante. Isso geralmente ocorre quando um investidor resgata seus tokens por dinheiro.',
    },
  ],
};

/**
 * FAQs por módulo da plataforma
 * Cada módulo possui pelo menos 5 perguntas frequentes
 */
export const moduleFaqs: Record<string, { question: string; answer: string }[]> = {
  banking: [
    {
      question: 'Como faço um PIX na plataforma?',
      answer: 'Acesse o menu Banking > PIX, informe a chave PIX do destinatário, o valor e confirme a transação. O PIX é processado em tempo real, 24 horas por dia.',
    },
    {
      question: 'Qual a diferença entre TED e transferência interna?',
      answer: 'A TED é uma transferência entre bancos diferentes, processada pelo Banco Central. A transferência interna ocorre entre contas do mesmo tenant, sendo instantânea e sem custo.',
    },
    {
      question: 'Como emitir um boleto?',
      answer: 'Acesse Banking > Boleto, preencha os dados do pagador, valor e vencimento. O boleto será gerado com código de barras e linha digitável para pagamento.',
    },
    {
      question: 'Posso agendar transferências recorrentes?',
      answer: 'Sim. Na tela de Transferências, selecione a opção "Recorrente" e defina a frequência (diária, semanal, mensal). As transferências serão executadas automaticamente.',
    },
    {
      question: 'Como consultar meu extrato bancário?',
      answer: 'Acesse Banking > Extrato para visualizar todas as movimentações da sua conta. Você pode filtrar por período, tipo de transação e exportar em PDF ou CSV.',
    },
  ],
  compliance: [
    {
      question: 'O que é PLD/FT e por que é importante?',
      answer: 'PLD/FT significa Prevenção à Lavagem de Dinheiro e Financiamento ao Terrorismo. É uma obrigação regulatória do Banco Central que exige monitoramento de transações suspeitas.',
    },
    {
      question: 'Como funciona a comunicação ao COAF?',
      answer: 'Quando uma transação é identificada como suspeita pelas regras PLD, uma comunicação é gerada automaticamente no formato exigido pelo COAF, podendo ser revisada antes do envio.',
    },
    {
      question: 'O que é uma Pessoa Politicamente Exposta (PEP)?',
      answer: 'PEP é uma pessoa que ocupa ou ocupou cargo público relevante nos últimos 5 anos, incluindo familiares e associados próximos. Transações de PEPs requerem monitoramento reforçado.',
    },
    {
      question: 'Como configurar regras de alerta PLD?',
      answer: 'Acesse Compliance > Regras PLD para criar regras baseadas em valor, frequência, horário ou padrão de transação. Cada regra define um threshold que, quando atingido, gera um alerta.',
    },
    {
      question: 'O que é o MED (Mecanismo Especial de Devolução)?',
      answer: 'O MED é um mecanismo do Banco Central para devolução de valores em casos de fraude ou falha operacional no PIX. Permite solicitar a devolução em até 80 dias.',
    },
  ],
  credit: [
    {
      question: 'Como funciona a análise de crédito?',
      answer: 'A análise considera o score interno do cliente, histórico de transações, dados de bureaus de crédito (Serasa, SPC) e as políticas de crédito configuradas pelo tenant.',
    },
    {
      question: 'Qual a diferença entre tabela SAC e Price?',
      answer: 'Na tabela SAC, as parcelas são decrescentes (amortização constante). Na tabela Price, as parcelas são fixas. O SAC resulta em menos juros totais, enquanto o Price oferece previsibilidade.',
    },
    {
      question: 'O que é o CET (Custo Efetivo Total)?',
      answer: 'O CET representa o custo total do empréstimo em percentual anual, incluindo juros, IOF, tarifas e seguros. É a forma mais transparente de comparar ofertas de crédito.',
    },
    {
      question: 'Como configurar políticas de crédito?',
      answer: 'Acesse Crédito > Políticas para definir regras de aprovação automática, limites por perfil de cliente, taxas de juros e prazos máximos. Cada tenant pode ter políticas independentes.',
    },
    {
      question: 'O que acontece em caso de inadimplência?',
      answer: 'O sistema aplica a régua de cobrança configurada: notificações automáticas, multa e juros de mora, e eventualmente negativação nos bureaus de crédito conforme as regras do tenant.',
    },
  ],
  cards: [
    {
      question: 'Como solicitar um cartão virtual?',
      answer: 'Acesse Cartões > Solicitar Cartão e escolha a opção "Virtual". O cartão é emitido instantaneamente com número, CVV e validade para uso imediato em compras online.',
    },
    {
      question: 'Posso ter mais de um cartão?',
      answer: 'Sim. Você pode ter múltiplos cartões virtuais e físicos vinculados à mesma conta. Cada cartão possui limite independente dentro do limite total aprovado.',
    },
    {
      question: 'Como bloquear meu cartão?',
      answer: 'Acesse Cartões > Meus Cartões, selecione o cartão desejado e clique em "Bloquear". O bloqueio é imediato e pode ser revertido a qualquer momento.',
    },
    {
      question: 'Como consultar a fatura do cartão?',
      answer: 'Acesse Cartões > Faturas para visualizar todas as compras do período atual e anteriores. Você pode pagar o valor total, mínimo ou parcelar o saldo devedor.',
    },
    {
      question: 'O que é o BIN do cartão?',
      answer: 'BIN (Bank Identification Number) são os primeiros 6 dígitos do cartão que identificam o emissor e a bandeira. É usado para roteamento de transações e identificação do produto.',
    },
  ],
  investments: [
    {
      question: 'O que é o perfil de investidor (Suitability)?',
      answer: 'É uma avaliação obrigatória que classifica seu perfil de risco (conservador, moderado ou arrojado) para recomendar produtos adequados e garantir conformidade regulatória.',
    },
    {
      question: 'Qual a diferença entre renda fixa e renda variável?',
      answer: 'Renda fixa tem rentabilidade previsível (CDB, LCI, LCA). Renda variável tem retorno incerto, dependendo do mercado (ações, fundos). A escolha depende do seu perfil de risco.',
    },
    {
      question: 'O que é o FGC?',
      answer: 'O Fundo Garantidor de Créditos protege investimentos em CDB, LCI, LCA e poupança até R$ 250.000 por CPF por instituição, em caso de falência do emissor.',
    },
    {
      question: 'Como funciona a liquidez dos investimentos?',
      answer: 'Liquidez é a facilidade de resgatar o investimento. Produtos com liquidez diária permitem resgate a qualquer momento. Outros têm carência ou só permitem resgate no vencimento.',
    },
    {
      question: 'Como simular um investimento?',
      answer: 'Acesse Investimentos > Simulador, escolha o produto, informe o valor e prazo. O sistema calcula a rentabilidade estimada, impostos e o valor líquido no vencimento.',
    },
  ],
  crypto: [
    {
      question: 'Como comprar criptomoedas na plataforma?',
      answer: 'Acesse Crypto > Marketplace, escolha a criptomoeda desejada, informe o valor em BRL e confirme a compra. A transação é processada via exchange integrada ao sistema.',
    },
    {
      question: 'O que é staking de criptomoedas?',
      answer: 'Staking é o processo de bloquear suas criptomoedas para validar transações na blockchain, recebendo recompensas em troca. É uma forma de renda passiva com ativos digitais.',
    },
    {
      question: 'Como funciona o yield farming?',
      answer: 'Yield farming consiste em fornecer liquidez a protocolos DeFi em troca de recompensas. Você deposita pares de tokens em pools e recebe taxas proporcionais ao seu depósito.',
    },
    {
      question: 'O que é uma bridge de criptomoedas?',
      answer: 'Bridge é um mecanismo que permite transferir tokens entre blockchains diferentes (ex: Ethereum para Polygon). Isso amplia as opções de uso e reduz custos de transação.',
    },
    {
      question: 'Como funciona o swap avançado?',
      answer: 'O swap avançado permite trocar criptomoedas usando agregadores DEX que buscam a melhor taxa entre múltiplas exchanges descentralizadas, otimizando o preço da operação.',
    },
  ],
  kyc: [
    {
      question: 'O que é KYC e por que preciso fazer?',
      answer: 'KYC (Know Your Customer) é o processo de verificação de identidade exigido por regulamentação. É necessário para garantir a segurança da plataforma e prevenir fraudes.',
    },
    {
      question: 'Quais documentos são aceitos para verificação?',
      answer: 'São aceitos RG, CNH, Passaporte e RNE. O documento deve estar válido, legível e com foto visível. Também é necessário um comprovante de residência recente (até 90 dias).',
    },
    {
      question: 'O que é a prova de vida (liveness)?',
      answer: 'É uma verificação biométrica que confirma que você é uma pessoa real e está presente no momento da verificação. Pode incluir desafios como piscar, sorrir ou virar a cabeça.',
    },
    {
      question: 'Quanto tempo leva a análise do KYC?',
      answer: 'A análise automática por OCR e bureaus leva segundos. Casos que requerem revisão manual pelo time de compliance podem levar até 48 horas úteis.',
    },
    {
      question: 'O que fazer se meu KYC for rejeitado?',
      answer: 'Verifique o motivo da rejeição na tela de status do KYC. Geralmente é necessário reenviar documentos com melhor qualidade ou corrigir dados divergentes.',
    },
  ],
  open_finance: [
    {
      question: 'O que é Open Finance?',
      answer: 'Open Finance é o sistema regulado pelo Banco Central que permite o compartilhamento padronizado de dados financeiros entre instituições, com o consentimento do cliente.',
    },
    {
      question: 'Como funciona o consentimento de dados?',
      answer: 'Você autoriza o compartilhamento de dados específicos (cadastro, contas, transações) com outras instituições por um período determinado. O consentimento pode ser revogado a qualquer momento.',
    },
    {
      question: 'O que é iniciação de pagamento via Open Finance?',
      answer: 'Permite que você inicie um PIX a partir de outra instituição financeira, sem precisar acessar o app do seu banco. O pagamento é processado diretamente pela instituição detentora da conta.',
    },
    {
      question: 'Meus dados estão seguros no Open Finance?',
      answer: 'Sim. O Open Finance segue padrões rigorosos de segurança (FAPI) definidos pelo Banco Central. Apenas dados autorizados são compartilhados e toda comunicação é criptografada.',
    },
    {
      question: 'Como revogar um consentimento?',
      answer: 'Acesse Open Finance > Consentimentos, selecione o consentimento ativo e clique em "Revogar". A revogação é imediata e a instituição receptora perde o acesso aos seus dados.',
    },
  ],
  security: [
    {
      question: 'Como ativar a autenticação de dois fatores (2FA)?',
      answer: 'Acesse Configurações > Segurança > 2FA. Você pode configurar via app autenticador (Google Authenticator, Authy) ou SMS. Recomendamos o app autenticador por ser mais seguro.',
    },
    {
      question: 'O que é verificação biométrica?',
      answer: 'É uma camada adicional de segurança que usa reconhecimento facial e/ou assinatura manuscrita para confirmar operações sensíveis como transferências de alto valor.',
    },
    {
      question: 'Como funciona a assinatura por voz (Voiceprint)?',
      answer: 'O sistema registra um perfil de voz único baseado nas características vocais do usuário. Em operações críticas, é solicitada uma frase de verificação para confirmar a identidade.',
    },
    {
      question: 'O que é a validação por geolocalização?',
      answer: 'O sistema verifica se a localização da operação está dentro de zonas permitidas. Transações de locais incomuns podem ser bloqueadas ou requerer verificação adicional.',
    },
    {
      question: 'Como funciona o registro em blockchain de assinaturas?',
      answer: 'Cada assinatura biométrica gera um hash registrado em blockchain EVM, criando um registro imutável e auditável de todas as autorizações realizadas na plataforma.',
    },
  ],
  admin: [
    {
      question: 'Como configurar um novo tenant?',
      answer: 'Acesse o Setup Wizard em Admin > Setup para configurar dados básicos, criar usuários, ativar módulos, configurar providers e personalizar o tema visual do tenant.',
    },
    {
      question: 'Como funciona o sistema de módulos?',
      answer: 'Cada tenant pode ativar ou desativar módulos conforme seu plano (Basic, Standard, Premium). Módulos desativados ficam invisíveis para os usuários do tenant.',
    },
    {
      question: 'O que é o Fee Cascade?',
      answer: 'É o sistema de cascata de taxas que permite configurar taxas em múltiplos níveis: plataforma, tenant e operação. As taxas são calculadas e distribuídas automaticamente.',
    },
    {
      question: 'Como gerenciar permissões de usuários?',
      answer: 'Acesse Admin > Permissões para configurar o RBAC (Role-Based Access Control). Cada perfil (Admin, Operator, Compliance, Customer) tem permissões predefinidas que podem ser customizadas.',
    },
    {
      question: 'Como monitorar a saúde do sistema?',
      answer: 'Acesse Admin > Monitoramento para visualizar o status de todos os módulos, providers e serviços. Alertas são gerados automaticamente quando problemas são detectados.',
    },
  ],
};
